import type { ServiceDetail } from "@/components/sections/service-detail";

type Category = {
  label: string;
  basePath: string; // e.g. "/marketing/local-seo"
  parentLabel?: string;
};

export function buildServiceHead(detail: ServiceDetail | undefined, category: Category) {
  if (!detail) {
    return {
      meta: [
        { title: `${category.label} Service — CoderLity` },
        { name: "robots", content: "noindex" },
      ],
    };
  }

  const url = `${category.basePath}/${detail.slug}`;
  const title = `${detail.title} Services | ${category.label} by CoderLity`;
  const description = `${detail.tagline} ${detail.description}`.slice(0, 158);
  const keywords = [
    detail.title,
    `${detail.title} services`,
    `${detail.title} agency`,
    `best ${detail.title}`,
    `${detail.title} near me`,
    category.label,
    ...(detail.bullets ?? []).slice(0, 4),
  ].join(", ");

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: detail.title,
    serviceType: detail.title,
    category: category.label,
    description: detail.description,
    url,
    provider: {
      "@type": "Organization",
      name: "CoderLity",
      url: "/",
    },
    areaServed: { "@type": "Place", name: "Worldwide" },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: `${detail.title} deliverables`,
      itemListElement: (detail.deliverables ?? []).map((d, i) => ({
        "@type": "Offer",
        position: i + 1,
        itemOffered: { "@type": "Service", name: d },
      })),
    },
  };

  const breadcrumbs = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "/" },
      { "@type": "ListItem", position: 2, name: "Marketing", item: "/services" },
      { "@type": "ListItem", position: 3, name: category.label, item: category.basePath },
      { "@type": "ListItem", position: 4, name: detail.title, item: url },
    ],
  };

  return {
    meta: [
      { title },
      { name: "description", content: description },
      { name: "keywords", content: keywords },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: url },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
    ],
    links: [{ rel: "canonical", href: url }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(serviceSchema) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbs) },
    ],
  };
}
